import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaTimes, FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import './css_pages/Products.css';
import {
  getAllProducts, createProduct, updateProduct, deleteProduct
} from '../services/productsService';
import { getAllProductTypes }  from '../services/productTypesService';
import { getAllCategories }     from '../services/categoriesServices';
import { getAllVendors }        from '../services/vendorsService';
import { importProducts }       from '../services/importService';
import { ALL_UNITS, CONTAINER_TYPES } from '../constants/units';
import { exportProductsToCSV }  from '../services/exportService';

const COUNT_BY_OPTIONS = [
  { value: 'Bottle',  label: 'Bottle'    },
  { value: 'Keg',     label: 'Keg'       },
  { value: 'Can',     label: 'Can'       },
  { value: 'Bag',     label: 'Bag'       },
  { value: 'Box',     label: 'Box'       },
  { value: 'Carton',  label: 'Carton'    },
  { value: 'Each',    label: 'Each'      },
  { value: 'L',       label: 'L (liter)' },
  { value: 'Gallon',  label: 'Gallon'    },
  { value: 'kg',      label: 'kg'        },
  { value: 'lb',      label: 'lb'        },
  { value: 'oz',      label: 'oz'        },
];

function Products() {
  const [products, setProducts]         = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [categories, setCategories]     = useState([]);
  const [vendors, setVendors]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  const [searchTerm, setSearchTerm]                   = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedSearchName, setAdvancedSearchName]   = useState('');
  const [advancedSearchCode, setAdvancedSearchCode]   = useState('');

  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [showModal, setShowModal]                     = useState(false);
  const [showDeleteModal, setShowDeleteModal]         = useState(false);
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal]     = useState(false);
  const [showVendorModal, setShowVendorModal]         = useState(false);
  const [deleteId, setDeleteId]                       = useState(null);
  const [editingId, setEditingId]                     = useState(null);

  const [productTypeSearch, setProductTypeSearch] = useState('');
  const [categorySearch, setCategorySearch]       = useState('');
  const [vendorSearch, setVendorSearch]           = useState('');

  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile]       = useState(null);
  const [importing, setImporting]             = useState(false);

  const emptyForm = {
    name: '', productCode: '', productType: null, category: null,
    containerType: '', countBy: '', containerSize: '', containerUnit: '',
    wholesalePrice: '', singlePortionSize: '', singlePortionUnit: '',
    fullWeight: '', fullWeightUnit: '', emptyWeight: '', emptyWeightUnit: '',
    caseSize: '', vendor: null
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchProducts(); fetchProductTypes(); fetchCategories(); fetchVendors();
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, advancedSearchCode]);

  useEffect(() => {
    const anyOpen = showModal || showDeleteModal || showProductTypeModal ||
      showCategoryModal || showVendorModal || showImportModal;
    if (anyOpen) document.body.classList.add('modal-open');
    else         document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showModal, showDeleteModal, showProductTypeModal, showCategoryModal, showVendorModal, showImportModal]);

  // ── Fetch ──
  const fetchProducts = async () => {
    try { setLoading(true); setError(null);
      const r = await getAllProducts();
      if (r.success) setProducts(r.data);
    } catch { setError('Error loading products'); }
    finally  { setLoading(false); }
  };
  const fetchProductTypes = async () => {
    try { const r = await getAllProductTypes(); if (r.success) setProductTypes(r.data); } catch {}
  };
  const fetchCategories = async () => {
    try { const r = await getAllCategories();   if (r.success) setCategories(r.data);   } catch {}
  };
  const fetchVendors = async () => {
    try { const r = await getAllVendors();      if (r.success) setVendors(r.data);      } catch {}
  };

  // ── Form handlers ──
  const resetForm   = () => setFormData(emptyForm);
  const handleInput = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleOpenModal  = ()    => { resetForm(); setEditingId(null); setShowModal(true); };
  const handleCloseModal = ()    => { setShowModal(false); resetForm(); setEditingId(null); };
  const handleEditModal  = (p)   => {
    setFormData({
      name:              p.name,
      productCode:       p.productCode       || '',
      productType:       p.productTypeId ? { id: p.productTypeId, name: p.productTypeName } : null,
      category:          p.categoryId   ? { id: p.categoryId,   name: p.categoryName   } : null,
      containerType:     p.containerType     || '',
      countBy:           p.countBy           || '',
      containerSize:     p.containerSize     || '',
      containerUnit:     p.containerUnit     || '',
      wholesalePrice:    p.wholesalePrice    || '',
      singlePortionSize: p.singlePortionSize || '',
      singlePortionUnit: p.singlePortionUnit || '',
      fullWeight:        p.fullWeight        || '',
      fullWeightUnit:    p.fullWeightUnit    || '',
      emptyWeight:       p.emptyWeight       || '',
      emptyWeightUnit:   p.emptyWeightUnit   || '',
      caseSize:          p.caseSize          || '',
      vendor:            p.vendorId ? { id: p.vendorId, name: p.vendorName } : null
    });
    setEditingId(p.id); setShowModal(true);
  };

  const handleOpenPT  = () => { setShowProductTypeModal(true); setProductTypeSearch(''); };
  const handleOpenCat = () => { setShowCategoryModal(true);    setCategorySearch(''); };
  const handleOpenVen = () => { setShowVendorModal(true);      setVendorSearch(''); };

  const handleSelectPT  = pt  => { handleInput('productType', pt);  setShowProductTypeModal(false); };
  const handleSelectCat = cat => { handleInput('category', cat);     setShowCategoryModal(false); };
  const handleSelectVen = v   => { handleInput('vendor', v);         setShowVendorModal(false); };

  // ── Save ──
  const handleSaveProduct = async () => {
    if (!formData.name.trim())                                        { alert('Name is required');          return; }
    if (!formData.productType)                                        { alert('Product type is required');  return; }
    if (!formData.category)                                           { alert('Category is required');      return; }
    if (!formData.containerType)                                      { alert('Container type is required'); return; }
    if (!formData.containerSize || parseFloat(formData.containerSize) <= 0) { alert('Container size must be > 0'); return; }
    if (!formData.containerUnit)                                      { alert('Container unit is required'); return; }
    if (!formData.wholesalePrice || parseFloat(formData.wholesalePrice) <= 0) { alert('Wholesale price is required'); return; }
    if (!formData.caseSize || parseInt(formData.caseSize) <= 0)      { alert('Case size is required');     return; }
    try {
      setLoading(true);
      const data = {
        name:              formData.name.trim(),
        productCode:       formData.productCode.trim() || null,
        productTypeId:     formData.productType.id,
        categoryId:        formData.category.id,
        containerType:     formData.containerType,
        countBy:           formData.countBy || null,
        containerSize:     parseFloat(formData.containerSize),
        containerUnit:     formData.containerUnit,
        wholesalePrice:    formData.wholesalePrice    ? parseFloat(formData.wholesalePrice)    : null,
        singlePortionSize: formData.singlePortionSize ? parseFloat(formData.singlePortionSize) : null,
        singlePortionUnit: formData.singlePortionUnit || null,
        fullWeight:        formData.fullWeight  !== '' && formData.fullWeight  != null ? parseFloat(formData.fullWeight)  : null,
        fullWeightUnit:    formData.fullWeightUnit  || null,
        emptyWeight:       formData.emptyWeight !== '' && formData.emptyWeight != null ? parseFloat(formData.emptyWeight) : null,
        emptyWeightUnit:   formData.emptyWeightUnit || null,
        caseSize:          formData.caseSize ? parseInt(formData.caseSize) : null,
        vendorId:          formData.vendor ? formData.vendor.id : null
      };
      if (editingId) {
        const r = await updateProduct(editingId, data);
        if (r.success) { alert('Product updated successfully'); await fetchProducts(); handleCloseModal(); }
      } else {
        const r = await createProduct(data);
        if (r.success) { alert('Product created successfully'); await fetchProducts(); handleCloseModal(); }
      }
    } catch (err) {
      if      (err.response?.status === 409) alert('A product with that name already exists');
      else if (err.response?.status === 400) alert(err.response.data.message || 'Validation error');
      else                                   alert('Error saving product');
    } finally { setLoading(false); }
  };

  // ── Delete ──
  const handleOpenDeleteModal  = id => { setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = ()  => { setShowDeleteModal(false); setDeleteId(null); };
  const handleConfirmDelete    = async () => {
    try {
      setLoading(true);
      const r = await deleteProduct(deleteId);
      if (r.success) { alert('Product deleted successfully'); await fetchProducts(); }
      handleCloseDeleteModal();
    } catch { alert('Error deleting product'); }
    finally { setLoading(false); }
  };

  // ── Filters & pagination ──
  const filteredProducts = products.filter(p => {
    const nm = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const cd = advancedSearchCode === '' ||
      (p.productCode && p.productCode.toLowerCase().includes(advancedSearchCode.toLowerCase()));
    return nm && cd;
  });

  const totalPages      = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex      = (currentPage - 1) * itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const getPageNumbers = () => {
    const pages = []; const max = 5;
    if (totalPages <= max) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3) { for (let i = 1; i <= 4; i++) pages.push(i); pages.push('...'); pages.push(totalPages); }
    else if (currentPage >= totalPages - 2) { pages.push(1); pages.push('...'); for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i); }
    else { pages.push(1); pages.push('...'); pages.push(currentPage-1); pages.push(currentPage); pages.push(currentPage+1); pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const filteredPT  = productTypes.filter(pt => pt.name.toLowerCase().includes(productTypeSearch.toLowerCase()));
  const filteredCat = categories.filter(c => {
    const ms = c.name.toLowerCase().includes(categorySearch.toLowerCase());
    const mt = !formData.productType || c.productTypeId === formData.productType.id;
    return ms && mt;
  });
  const filteredVen = vendors.filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()));

  // ── Import / Export ──
  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    const valid = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/vnd.ms-excel'];
    if (valid.includes(file.type)) setSelectedFile(file);
    else { alert('Please select a valid Excel file (.xlsx or .xls)'); e.target.value = ''; }
  };

  const handleImport = async () => {
    if (!selectedFile) { alert('Please select a file'); return; }
    try {
      setImporting(true);
      const result = await importProducts(selectedFile, () => {});
      if (result.success) {
        alert(`Import completed!\nTotal: ${result.stats.total}\nImported: ${result.stats.imported}\nSkipped: ${result.stats.skipped}`);
        await fetchProducts(); handleCloseImportModal();
      }
    } catch (err) { alert('Error importing: ' + (err.response?.data?.message || err.message)); }
    finally { setImporting(false); }
  };
  const handleCloseImportModal = () => { setShowImportModal(false); setSelectedFile(null); };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const result = await exportProductsToCSV();
      if (result.success) {
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url  = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        alert('Products exported successfully!');
      } else { alert('Error exporting: ' + result.message); }
    } catch { alert('Error exporting products'); }
    finally { setLoading(false); }
  };

  // ── Render ──
  return (
    <div className="products-page">

      {/* ── HEADER ── */}
      <div className="products-page-header products-sticky-header">
        <div className="header-left">
          <h1>Products</h1>
        </div>
        <div className="products-header-buttons">
          <button className="products-btn-secondary" onClick={handleExportCSV} disabled={loading}>
            <FaPlus /> Export to CSV
          </button>
          <button className="products-btn-secondary" onClick={() => setShowImportModal(true)} disabled={loading}>
            <FaPlus /> Import from Excel
          </button>
          <button className="products-btn-primary" onClick={handleOpenModal} disabled={loading}>
            <FaPlus /> Create new product
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchProducts}>Retry</button>
        </div>
      )}

      {/* ── SEARCH ── */}
      <div className="products-search-section">
        <div className="products-search-bar">
          <input type="text" placeholder="Search by name..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {/* ── ADVANCED FILTERS ── */}
      <div className="products-filters-toggle">
        <button className="products-btn-toggle-filters"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
          {showAdvancedFilters ? '▼' : '▶'} Show advanced filters
        </button>
      </div>

      {showAdvancedFilters && (
        <div className="products-advanced-filters">
          <div className="products-filter-row">
            <div className="products-form-group">
              <label>Name</label>
              <input type="text" value={advancedSearchName}
                onChange={e => setAdvancedSearchName(e.target.value)} placeholder="Search by name..." />
            </div>
            <div className="products-form-group">
              <label>Product Code</label>
              <input type="text" value={advancedSearchCode}
                onChange={e => setAdvancedSearchCode(e.target.value)} placeholder="Search by code..." />
            </div>
          </div>
          <div className="products-filter-buttons">
            <button className="products-btn-search"
              onClick={() => setSearchTerm(advancedSearchName)}>
              <FaSearch /> Search
            </button>
            <button className="products-btn-secondary"
              onClick={() => { setAdvancedSearchName(''); setAdvancedSearchCode(''); setSearchTerm(''); }}>
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* ── PAGINATION INFO ── */}
      <div className="products-pagination-info">
        <span>Total: {filteredProducts.length}</span>
        <span className="sep">|</span>
        <span>
          Showing {startIndex + 1} – {Math.min(startIndex + itemsPerPage, filteredProducts.length)} of {filteredProducts.length}
        </span>
        <div className="products-items-per-page">
          <label>Items per page:</label>
          <select value={itemsPerPage}
            onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* ── TABLE DESKTOP ── */}
      <div className="products-table-wrap products-table-desktop">
        {loading && <div className="products-loading">Loading…</div>}
        <table className="products-data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Container<br/>Size</th>
              <th>S.<br/>Unit</th>
              <th>WS</th>
              <th>Container<br/>Type</th>
              <th>Count<br/>By</th>
              <th>Case</th>
              <th>Full</th>
              <th>Full<br/>Unit</th>
              <th>Empty</th>
              <th>Empty<br/>Unit</th>
              <th>Single<br/>Portion<br/>Size</th>
              <th>Single<br/>Unit</th>
              <th>Vendor</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentProducts.length === 0 ? (
              <tr>
                <td colSpan="16" style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>
                  {loading ? 'Loading…' : 'No results found'}
                </td>
              </tr>
            ) : currentProducts.map(p => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{p.categoryName}</td>
                <td>{p.containerSize}</td>
                <td>{p.containerUnit}</td>
                <td>{p.wholesalePrice ? `$${parseFloat(p.wholesalePrice).toFixed(2)}` : '-'}</td>
                <td>{p.containerType}</td>
                <td>{p.countBy || '-'}</td>
                <td>{p.caseSize || '-'}</td>
                <td>{p.fullWeight || '-'}</td>
                <td>{p.fullWeightUnit || '-'}</td>
                <td>{p.emptyWeight || '-'}</td>
                <td>{p.emptyWeightUnit || '-'}</td>
                <td>{p.singlePortionSize || '-'}</td>
                <td>{p.singlePortionUnit || '-'}</td>
                <td>{p.vendorName || '-'}</td>
                <td>
                  {/* Ambos botones siempre visibles gracias a la columna de 11% */}
                  <div className="products-actions-cell">
                    <button className="products-btn-edit"
                      onClick={() => handleEditModal(p)} disabled={loading}>
                      Edit
                    </button>
                    <button className="products-btn-del"
                      onClick={() => handleOpenDeleteModal(p.id)} disabled={loading}
                      title="Delete">
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── CARDS MOBILE ── */}
      <div className="products-cards-wrap">
        {loading && <div className="products-loading">Loading…</div>}
        {currentProducts.length === 0 ? (
          <div className="products-no-results">{loading ? 'Loading…' : 'No results found'}</div>
        ) : currentProducts.map(p => (
          <div key={p.id} className="product-card">
            <div className="product-card-header">
              <h3 className="product-card-name">{p.name}</h3>
              <span className="product-card-category">{p.categoryName}</span>
            </div>
            <div className="product-card-body">
              <div className="product-card-row">
                <div className="product-card-item">
                  <span className="product-card-label">Size</span>
                  <span className="product-card-value">{p.containerSize} {p.containerUnit}</span>
                </div>
                <div className="product-card-item">
                  <span className="product-card-label">WS</span>
                  <span className="product-card-value highlight">
                    {p.wholesalePrice ? `$${parseFloat(p.wholesalePrice).toFixed(2)}` : '-'}
                  </span>
                </div>
              </div>
              <div className="product-card-row">
                <div className="product-card-item">
                  <span className="product-card-label">Container</span>
                  <span className="product-card-value">{p.containerType}</span>
                </div>
                <div className="product-card-item">
                  <span className="product-card-label">Count By</span>
                  <span className="product-card-value">{p.countBy || '-'}</span>
                </div>
              </div>
              <div className="product-card-row">
                <div className="product-card-item">
                  <span className="product-card-label">Case</span>
                  <span className="product-card-value">{p.caseSize || '-'}</span>
                </div>
                <div className="product-card-item">
                  <span className="product-card-label">Full</span>
                  <span className="product-card-value">
                    {p.fullWeight ? `${p.fullWeight} ${p.fullWeightUnit}` : '-'}
                  </span>
                </div>
              </div>
              <div className="product-card-row">
                <div className="product-card-item">
                  <span className="product-card-label">Empty</span>
                  <span className="product-card-value">
                    {p.emptyWeight ? `${p.emptyWeight} ${p.emptyWeightUnit}` : '-'}
                  </span>
                </div>
                <div className="product-card-item">
                  <span className="product-card-label">Single Portion</span>
                  <span className="product-card-value">
                    {p.singlePortionSize ? `${p.singlePortionSize} ${p.singlePortionUnit}` : '-'}
                  </span>
                </div>
              </div>
              {p.vendorName && (
                <div className="product-card-row">
                  <div className="product-card-item full-width">
                    <span className="product-card-label">Vendor</span>
                    <span className="product-card-value">{p.vendorName}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="product-card-footer">
              <button className="products-btn-card edit"
                onClick={() => handleEditModal(p)} disabled={loading}>Edit</button>
              <button className="products-btn-card delete"
                onClick={() => handleOpenDeleteModal(p.id)} disabled={loading}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* ── PAGINATION CONTROLS ── */}
      {totalPages > 1 && (
        <div className="products-pagination-controls">
          <button className="products-page-btn" onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1} title="First">⟨⟨</button>
          <button className="products-page-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))}
            disabled={currentPage === 1}><FaChevronLeft /></button>
          <div className="products-page-numbers">
            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span key={`e${i}`} className="products-page-ellipsis">…</span>
              ) : (
                <button key={page}
                  className={`products-page-num ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}>{page}</button>
              )
            )}
          </div>
          <button className="products-page-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))}
            disabled={currentPage === totalPages}><FaChevronRight /></button>
          <button className="products-page-btn" onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages} title="Last">⟩⟩</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          MODAL CREATE / EDIT
          ══════════════════════════════════════════════ */}
      {showModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-large" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>{editingId ? 'Edit product' : 'Create new product'}</h2>
              <button className="products-btn-close" onClick={handleCloseModal}><FaTimes /></button>
            </div>
            <div className="products-modal-body">

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name}
                    onChange={e => handleInput('name', e.target.value)}
                    placeholder="Enter product name" disabled={loading} />
                </div>
                <div className="products-form-group">
                  <label>Product code</label>
                  <input type="text" value={formData.productCode}
                    onChange={e => handleInput('productCode', e.target.value)}
                    placeholder="Product code" disabled={loading} />
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Product Type *</label>
                  <div className="products-pick-container">
                    <button className="products-btn-pick" onClick={handleOpenPT} disabled={loading} type="button">
                      <FaSearch /> Pick
                    </button>
                    {formData.productType && (
                      <div className="products-selected-item">
                        {formData.productType.name}
                        <button className="products-btn-remove-sel"
                          onClick={() => handleInput('productType', null)} type="button"><FaTimes /></button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="products-form-group">
                  <label>Product Category *</label>
                  <div className="products-pick-container">
                    <button className="products-btn-pick" onClick={handleOpenCat} disabled={loading} type="button">
                      <FaSearch /> Pick
                    </button>
                    {formData.category && (
                      <div className="products-selected-item">
                        {formData.category.name}
                        <button className="products-btn-remove-sel"
                          onClick={() => handleInput('category', null)} type="button"><FaTimes /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Container Type *</label>
                  <select value={formData.containerType}
                    onChange={e => handleInput('containerType', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {CONTAINER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="products-form-group">
                  <label>Count By</label>
                  <select value={formData.countBy}
                    onChange={e => handleInput('countBy', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {COUNT_BY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="products-form-row products-form-row-3">
                <div className="products-form-group">
                  <label>Container size *</label>
                  <input type="number" step="1" value={formData.containerSize}
                    onChange={e => handleInput('containerSize', e.target.value)}
                    placeholder="0" disabled={loading} />
                </div>
                <div className="products-form-group">
                  <label>Units *</label>
                  <select value={formData.containerUnit}
                    onChange={e => handleInput('containerUnit', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {ALL_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div className="products-form-group">
                  <label>Wholesale container price</label>
                  <div className="products-input-prefix-wrap">
                    <span className="products-input-prefix">$</span>
                    <input type="number" step="1" value={formData.wholesalePrice}
                      onChange={e => handleInput('wholesalePrice', e.target.value)}
                      placeholder="0.00" disabled={loading} />
                  </div>
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Single portion size</label>
                  <input type="number" step="1" value={formData.singlePortionSize}
                    onChange={e => handleInput('singlePortionSize', e.target.value)}
                    placeholder="0" disabled={loading} />
                </div>
                <div className="products-form-group">
                  <label>Single portion unit</label>
                  <select value={formData.singlePortionUnit}
                    onChange={e => handleInput('singlePortionUnit', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {ALL_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Full weight</label>
                  <input type="number" step="1" value={formData.fullWeight}
                    onChange={e => handleInput('fullWeight', e.target.value)}
                    placeholder="0" disabled={loading} />
                </div>
                <div className="products-form-group">
                  <label>Full weight unit</label>
                  <select value={formData.fullWeightUnit}
                    onChange={e => handleInput('fullWeightUnit', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {ALL_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Empty weight</label>
                  <input type="number" step="1" value={formData.emptyWeight}
                    onChange={e => handleInput('emptyWeight', e.target.value)}
                    placeholder="0" disabled={loading} />
                </div>
                <div className="products-form-group">
                  <label>Empty weight unit</label>
                  <select value={formData.emptyWeightUnit}
                    onChange={e => handleInput('emptyWeightUnit', e.target.value)} disabled={loading}>
                    <option value="">Select…</option>
                    {ALL_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Case size</label>
                  <input type="number" value={formData.caseSize}
                    onChange={e => handleInput('caseSize', e.target.value)}
                    placeholder="0" disabled={loading} />
                </div>
              </div>

              <div className="products-form-row">
                <div className="products-form-group">
                  <label>Vendor</label>
                  <div className="products-pick-container">
                    <button className="products-btn-pick" onClick={handleOpenVen} disabled={loading} type="button">
                      <FaSearch /> Pick
                    </button>
                    {formData.vendor && (
                      <div className="products-selected-item">
                        {formData.vendor.name}
                        <button className="products-btn-remove-sel"
                          onClick={() => handleInput('vendor', null)} type="button"><FaTimes /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
            <div className="products-modal-footer">
              <button className="products-btn-secondary" onClick={handleCloseModal} disabled={loading}>Cancel</button>
              <button className="products-btn-primary"   onClick={handleSaveProduct} disabled={loading}>
                {loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PRODUCT TYPE ── */}
      {showProductTypeModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-picker" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>Select Product Type</h2>
              <button className="products-btn-close" onClick={() => setShowProductTypeModal(false)}><FaTimes /></button>
            </div>
            <div className="products-modal-body">
              <div className="products-form-group">
                <input type="text" placeholder="Search…" value={productTypeSearch}
                  onChange={e => setProductTypeSearch(e.target.value)} autoFocus />
              </div>
              <div className="products-picker-list">
                <table className="products-picker-table">
                  <thead><tr><th>Select</th><th>Name</th></tr></thead>
                  <tbody>
                    {filteredPT.map(pt => (
                      <tr key={pt.id}>
                        <td><button className="products-btn-select" onClick={() => handleSelectPT(pt)}>✓</button></td>
                        <td>{pt.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CATEGORY ── */}
      {showCategoryModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-picker" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>Select Category</h2>
              <button className="products-btn-close" onClick={() => setShowCategoryModal(false)}><FaTimes /></button>
            </div>
            <div className="products-modal-body">
              <div className="products-form-group">
                <input type="text" placeholder="Search…" value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)} autoFocus />
              </div>
              <div className="products-picker-list">
                <table className="products-picker-table">
                  <thead><tr><th>Select</th><th>Name</th></tr></thead>
                  <tbody>
                    {filteredCat.map(c => (
                      <tr key={c.id}>
                        <td><button className="products-btn-select" onClick={() => handleSelectCat(c)}>✓</button></td>
                        <td>{c.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL VENDOR ── */}
      {showVendorModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-picker" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>Select Vendor</h2>
              <button className="products-btn-close" onClick={() => setShowVendorModal(false)}><FaTimes /></button>
            </div>
            <div className="products-modal-body">
              <div className="products-form-group">
                <input type="text" placeholder="Search…" value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)} autoFocus />
              </div>
              <div className="products-picker-list">
                <table className="products-picker-table">
                  <thead><tr><th>Select</th><th>Name</th></tr></thead>
                  <tbody>
                    {filteredVen.map(v => (
                      <tr key={v.id}>
                        <td><button className="products-btn-select" onClick={() => handleSelectVen(v)}>✓</button></td>
                        <td>{v.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DELETE ── */}
      {showDeleteModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-small" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>Confirm deletion</h2>
              <button className="products-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
            </div>
            <div className="products-modal-body">
              <p>Are you sure you want to delete this product?</p>
              <p className="products-warning-text">This action cannot be undone.</p>
            </div>
            <div className="products-modal-footer">
              <button className="products-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
              <button className="products-btn-danger"    onClick={handleConfirmDelete}    disabled={loading}>
                {loading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL IMPORT ── */}
      {showImportModal && (
        <div className="products-modal-overlay">
          <div className="products-modal-content products-modal-small" onClick={e => e.stopPropagation()}>
            <div className="products-modal-header">
              <h2>Import Products from Excel</h2>
              <button className="products-btn-close" onClick={handleCloseImportModal}><FaTimes /></button>
            </div>
            <div className="products-modal-body">
              <div className="products-form-group">
                <label>Select Excel File *</label>
                <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect}
                  disabled={importing} className="products-file-input" />
                {selectedFile && (
                  <p style={{ marginTop:'8px', fontSize:'13px', color:'#64748b' }}>
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>
              {importing && (
                <div style={{ display:'flex', justifyContent:'center', padding:'16px' }}>
                  <div className="loading-spinner" />
                </div>
              )}
              <div className="products-form-hint">
                <strong>Required columns:</strong> Product Name, Product Type, Category, Container type, Size, Units
              </div>
            </div>
            <div className="products-modal-footer">
              <button className="products-btn-secondary" onClick={handleCloseImportModal} disabled={importing}>Cancel</button>
              <button className="products-btn-primary"   onClick={handleImport} disabled={importing || !selectedFile}>
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Products;